const possibleIncrements = [5.0, 2.5, 1.25, 1, 0.5, 0.25, 0.125];

export function rewriteVulgarFraction(weight) {
  /** @type {String} */
  let ret = weight;
  if (ret.endsWith(".5")) {
    ret = ret.replace(".5", "½");
  } else if (ret.endsWith(".25")) {
    ret = ret.replace(".25", "¼");
  } else if (ret.endsWith(".75")) {
    ret = ret.replace(".75", "¾");
  } else if (ret.endsWith(".125")) {
    ret = ret.replace(".125", "⅛")
  }

  if (ret.startsWith("0")) {
    ret = ret.replace("0", "");
  }
  return ret;
}


/// Compute the plate set effective GCD.
///
/// The weights aren't integers so instead this is a pretty easy way to find what
/// we can all divide them by.
function computeIncrement(plateTypes) {
  for (let i = 0; i < possibleIncrements.length; i++) {
    let increment = possibleIncrements[i];
    if (plateTypes.every((plate) => plate % increment === 0)) {
      return increment;
    }
  }

  throw Error("Couldn't find increment for plate set :" + plateTypes.toString());
}

/// Compute a mapping of { weight (or rather, increment count) : plate list }.
function computeMapping(increment, plates) {
  const knapsack = plates.map((p) => p / increment);

  // We now do a modified 0/1 Knapsack where we want to minimize the amount of
  // plates for each weight.

  // The first axis is the weight (or rather, weightIncrement count)
  // Second axis is the "possible new plate"
  // Each slot contains a list representing the plates put in so far. Again,
  // we strive to minize the amount of plate, or the length of this array.
  const dp = [];

  // The max in increment given the plates we have. This will be used to limit
  // the weights we try to build.
  let currentMaxIncrement = 0;
  for (let j = 0; j < knapsack.length; j++) {
    let newElement = knapsack[j];
    currentMaxIncrement += newElement;

    // Create the full row up to the max we can possibly reach
    dp.push((new Array(currentMaxIncrement + 1)).fill([]));

    // Copy all the stuff from before the new element.
    if (j !== 0) {
      for (let i = 0; i < newElement && i < dp[j - 1].length; i++) {
        dp[j][i] = dp[j - 1][i];
      }
    }

    // For all the possible weights heavier than the current plate, figure out
    // the plating for that weight.
    //
    // It should either be the plating that was already computed for this weight,
    // or the plating for (weight - plateWeight) to which we add the current plate.
    for (let i = newElement; i <= currentMaxIncrement; i++) {
      if (i === newElement) {
        // Basically, if the weight is the plate, just put the plate in.
        dp[j][i] = [i];
        continue;
      }

      let platesForWeightWithoutNewElements;

      // Get the plates that were calculated for this weight without the
      // current plate.
      if (!(j === 0 || dp[j - 1].length - 1 < i)) {
        platesForWeightWithoutNewElements = dp[j - 1][i];
      }

      let platesForLowerWeight = dp[j - 1][i - newElement];

      if (platesForLowerWeight.length === 0 &&
        platesForWeightWithoutNewElements == null) {
        // This means that we can't add the plate nor use a previous set.
        // We'll just leave this "cell" as is then.
        continue;
      } else if (platesForWeightWithoutNewElements == null) {
        let newPlates = platesForLowerWeight.slice();
        newPlates.push(newElement);

        dp[j][i] = newPlates;
      } else if (platesForLowerWeight.length === 0) {
        dp[j][i] = platesForWeightWithoutNewElements;
      } else {
        if (platesForLowerWeight.length + 1 <=
          platesForWeightWithoutNewElements.length) {
          let newPlates = platesForLowerWeight.slice();
          newPlates.push(newElement);

          dp[j][i] = newPlates;
        } else {
          dp[j][i] = platesForWeightWithoutNewElements;
        }
      }
    }
  }

  // Now the final row should be what we want, basically a mapping of weight increment
  // to plating (also in increment). We then modify the plating to be in actual weight
  // since this is what the user cares about.
  return dp[dp.length - 1].map((incrementList) => incrementList.map((inc) => inc * increment));
}

class PlateCalculator {
  constructor(barbellWeight, plateMapping) {
    this.barbellWeight = barbellWeight;

    /// An unsorted list of all the different weights.
    const plateTypes = [];

    /// A sorted list of all the plates. For example, if we have 3 10lbs plates and 2
    /// 5lbs plates, the list would be [10, 10, 10, 5, 5].
    const plates = [];
    for (let plateWeight in plateMapping) {
      const weight = parseFloat(plateWeight);
      if (plateMapping[plateWeight] !== 0) {
        plateTypes.push(weight);
      }

      plates.push(...(new Array(plateMapping[plateWeight])).fill(weight));
    }
    // .sort() by default sorts lexicographically. Even numbers...
    plates.sort((a, b) => a - b);

    this.increment = computeIncrement(plateTypes);
    this.weightMapping = computeMapping(this.increment, plates);
  }

  findClosestWeight(weightIncrement) {
    for (let dist = 0; dist <= weightIncrement; dist++) {
      if (weightIncrement + dist < this.weightMapping.length &&
        this.weightMapping[weightIncrement + dist].length !== 0) {
        return weightIncrement + dist;
      } else if (this.weightMapping[weightIncrement + dist].length !== 0) {
        return weightIncrement - dist;
      }
    }

    return null;
  }

  /// Returns the closest weight that is doable with the given barbell and plates.
  roundWeight(weight) {
    weight -= this.barbellWeight;
    // The plating is calculated using only one plate on each side. Hence why we
    // half the weight.
    weight /= 2;

    let weightInIncrement = Math.ceil(weight / this.increment);

    if (weightInIncrement >= this.weightMapping.length) {
      return (this.weightMapping.length - 1) * 2 * this.increment + this.barbellWeight;
    }

    let closestWeight = this.findClosestWeight(weightInIncrement);
    if (!closestWeight) {
      return this.barbellWeight;
    }
    return closestWeight * 2 * this.increment + this.barbellWeight;
  }

  platingFor(weight) {
    if (!weight) {
      return [];
    }

    weight -= this.barbellWeight;
    // The plating is calculated using only one plate on each side. Hence why we
    // half the weight.
    weight /= 2;

    let weightInIncrement = Math.ceil(weight / this.increment);

    if (weightInIncrement >= this.weightMapping.length) {
      return this.weightMapping[this.weightMapping.length - 1].slice().reverse();
    }

    let closestWeight = this.findClosestWeight(weightInIncrement);
    if (!closestWeight) {
      return [];
    }

    return this.weightMapping[closestWeight].slice().reverse();
  }
}

export default PlateCalculator;